import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import {
  Plus, Search, Trash2, ChevronDown, ChevronRight, List, BarChart3,
  Users, User, LayoutGrid, Clock, CheckCircle2, AlertTriangle,
  MoreHorizontal, ArrowUpDown, Eye, Copy, Play, Pause, RotateCcw,
  Calendar, Flame, X
} from "lucide-react";
import { toast } from "sonner";
import usePullToRefresh from "../hooks/usePullToRefresh";
import MobileSkeleton from "../components/MobileSkeleton";
import useIsMobile from "../hooks/useIsMobile";
import useShortcuts from "../hooks/useShortcuts";
import useUrlState from "../hooks/useUrlState";
import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_DOT_COLORS,
  STATUS_PILL_CLASSES as STATUS_COLORS,
  PRIORITY_COLORS,
  STATUS_KEY_MAP,
  PRIORITY_KEY_MAP,
} from "../lib/statusColors";

/* Kept as alias for the gantt view — same palette as dots */
const GANTT_STATUS_COLORS = STATUS_DOT_COLORS;

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */
function getDueInfo(dueDateStr) {
  if (!dueDateStr) return null;
  try {
    const now = new Date(); now.setHours(0,0,0,0);
    const due = new Date(dueDateStr + "T00:00:00"); due.setHours(0,0,0,0);
    const diff = Math.round((due - now) / 86400000);
    const formatted = due.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    if (diff < -1) return { text: `${Math.abs(diff)}d overdue`, color: "text-red-400", bg: "bg-red-500/10", formatted, overdue: true, diff };
    if (diff === -1) return { text: "1d overdue", color: "text-red-400", bg: "bg-red-500/10", formatted, overdue: true, diff };
    if (diff === 0) return { text: "Today", color: "text-amber-400", bg: "bg-amber-500/10", formatted, today: true, diff };
    if (diff === 1) return { text: "Tomorrow", color: "text-amber-300", bg: "bg-amber-500/8", formatted, diff };
    if (diff <= 3) return { text: `in ${diff}d`, color: "text-yellow-400", bg: "bg-yellow-500/8", formatted, diff };
    if (diff <= 7) return { text: `in ${diff}d`, color: "text-green-400", bg: "bg-green-500/6", formatted, diff };
    return { text: null, formatted, diff };
  } catch { return null; }
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

const AVATAR_COLORS = [
  { bg: "rgba(59,130,246,0.12)", color: "#60A5FA" },
  { bg: "rgba(227,25,55,0.12)", color: "#E31937" },
  { bg: "rgba(139,92,246,0.12)", color: "#A78BFA" },
  { bg: "rgba(245,158,11,0.12)", color: "#FBBF24" },
  { bg: "rgba(6,182,212,0.12)", color: "#06B6D4" },
  { bg: "rgba(34,197,94,0.12)", color: "#4ADE80" },
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/* ═══════════════════════════════════════
   STATUS DROPDOWN (inline click)
   ═══════════════════════════════════════ */
function StatusDropdown({ taskId, currentStatus, onUpdate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const handleChange = async (e, newStatus) => {
    e.stopPropagation();
    setOpen(false);
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      onUpdate();
      toast.success(`Status → ${newStatus}`);
    } catch { toast.error("Failed to update status"); }
  };
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors cursor-pointer hover:brightness-125 ${STATUS_COLORS[currentStatus] || "bg-[var(--s2)] text-[var(--t2)]"}`}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT_COLORS[currentStatus] }} />
        {currentStatus}
        <ChevronDown size={12} className="opacity-40" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-xl py-1 min-w-[130px]">
          {STATUS_OPTIONS.map(s => (
            <button key={s} onClick={(e) => handleChange(e, s)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--s2)] transition-colors flex items-center gap-2 ${s === currentStatus ? "text-[var(--t1)] font-medium" : "text-[#999]"}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT_COLORS[s] }} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   PRIORITY DROPDOWN (inline click)
   ═══════════════════════════════════════ */
function PriorityDropdown({ taskId, currentPriority, onUpdate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const handleChange = async (e, newPriority) => {
    e.stopPropagation();
    setOpen(false);
    try {
      await api.put(`/tasks/${taskId}`, { priority: newPriority });
      onUpdate();
      toast.success(`Priority → ${newPriority}`);
    } catch { toast.error("Failed to update priority"); }
  };
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-xs px-2.5 py-1 rounded flex items-center gap-1.5 transition-colors cursor-pointer hover:brightness-125"
        style={{ background: (PRIORITY_COLORS[currentPriority] || "#666") + "18", color: PRIORITY_COLORS[currentPriority] || "#666" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY_COLORS[currentPriority] }} />
        {currentPriority}
        <ChevronDown size={11} className="opacity-40" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-xl py-1 min-w-[110px]">
          {["Urgent", "High", "Medium", "Low"].map(p => (
            <button key={p} onClick={(e) => handleChange(e, p)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--s2)] transition-colors flex items-center gap-2 ${p === currentPriority ? "font-medium" : ""}`}
              style={{ color: PRIORITY_COLORS[p] }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY_COLORS[p] }} />
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   CONTEXT MENU (right-click)
   ═══════════════════════════════════════ */
function ContextMenu({ x, y, task, onClose, onAction }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const items = [
    { icon: Eye, label: "Open task", action: "open" },
    { icon: Copy, label: "Copy link", action: "copy" },
    { divider: true },
    ...(task.status !== "In Progress" ? [{ icon: Play, label: "Start task", action: "start" }] : []),
    ...(task.status === "In Progress" ? [{ icon: Pause, label: "Pause", action: "pause" }] : []),
    ...(task.status !== "Review" ? [{ icon: RotateCcw, label: "Send to review", action: "review" }] : []),
    ...(task.status !== "Done" ? [{ icon: CheckCircle2, label: "Mark done", action: "done", color: "text-green-400" }] : []),
    { divider: true },
    { icon: Trash2, label: "Delete", action: "delete", color: "text-red-400" },
  ];

  return (
    <div ref={ref} className="fixed z-[100] bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-2xl py-1 min-w-[180px] dm-context-menu"
      style={{ left: Math.min(x, window.innerWidth - 200), top: Math.min(y, window.innerHeight - 300) }}>
      {items.map((item, i) => item.divider ? (
        <div key={i} className="h-px bg-[var(--s2)] my-1" />
      ) : (
        <button key={i} onClick={() => { onAction(item.action, task); onClose(); }}
          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--s2)] transition-colors flex items-center gap-2 ${item.color || "text-[#999]"}`}>
          <item.icon size={14} /> {item.label}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   HOVER PREVIEW TOOLTIP
   ═══════════════════════════════════════ */
function HoverPreview({ task, x, y }) {
  if (!task) return null;
  const subtaskCount = (task.subtasks || []).length;
  const subtaskDone = (task.subtasks || []).filter(s => s.done).length;
  return (
    <div className="fixed z-[90] bg-[var(--s1)] border border-[var(--bdrh)] rounded-xl shadow-2xl p-4 w-[300px] pointer-events-none"
      style={{ left: Math.min(x + 16, window.innerWidth - 320), top: Math.min(y - 20, window.innerHeight - 250) }}>
      <p className="text-sm font-medium text-white/90 mb-2">{task.title}</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {task.brief_title && <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400">{task.brief_title}</span>}
        {task.lead_name && <span className="text-xs px-2 py-0.5 rounded bg-purple-400/10 text-purple-400">{task.lead_name}</span>}
        {task.project_name && <span className="text-xs px-2 py-0.5 rounded bg-blue-400/10 text-blue-400">{task.project_name}</span>}
      </div>
      {task.description && <p className="text-xs text-[var(--t2)] mb-2 line-clamp-2">{task.description}</p>}
      {subtaskCount > 0 && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-[10px] text-[var(--t2)] mb-1">
            <span>Subtasks</span><span>{subtaskDone}/{subtaskCount}</span>
          </div>
          <div className="h-1.5 bg-[var(--s2)] rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${subtaskCount > 0 ? (subtaskDone / subtaskCount) * 100 : 0}%` }} />
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 text-xs text-[var(--t3)]">
        {task.assignee_name && <span>Assigned to {task.assignee_name}</span>}
        {task.created_by_name && <span>Created by {task.created_by_name}</span>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   BULK ACTIONS BAR
   ═══════════════════════════════════════ */
function BulkActionsBar({ count, onAction, onClear }) {
  if (count === 0) return null;
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-[var(--s1)] border border-[var(--bdrh)] rounded-xl shadow-2xl backdrop-blur-sm"
      style={{ animation: "slideUp .2s ease-out" }}>
      <span className="text-sm text-[var(--t1)] font-medium">{count} selected</span>
      <div className="w-px h-5 bg-[var(--s3)]" />
      <button onClick={() => onAction("status")} className="text-sm px-3 py-1.5 rounded-md bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">Change Status</button>
      <button onClick={() => onAction("priority")} className="text-sm px-3 py-1.5 rounded-md bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors">Set Priority</button>
      <button onClick={() => onAction("done")} className="text-sm px-3 py-1.5 rounded-md bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors flex items-center gap-1"><CheckCircle2 size={12} /> Done</button>
      <button onClick={() => onAction("delete")} className="text-sm px-3 py-1.5 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-1"><Trash2 size={12} /> Delete</button>
      <div className="w-px h-5 bg-[var(--s3)]" />
      <button onClick={onClear} className="text-xs text-[#666] hover:text-[var(--t1)] transition-colors"><X size={14} /></button>
    </div>
  );
}

/* ═══════════════════════════════════════
   WORKLOAD HEATMAP
   ═══════════════════════════════════════ */
function WorkloadHeatmap({ tasks }) {
  const data = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (t.assignee_name && t.status !== "Done") {
        const key = t.assignee_name;
        if (!map[key]) map[key] = { name: key, count: 0 };
        map[key].count++;
      }
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [tasks]);
  if (data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-center gap-3 ml-auto">
      <span className="text-xs text-[#666] font-semibold tracking-wider font-heading">WORKLOAD</span>
      <div className="flex gap-1.5 items-end h-6">
        {data.map(d => (
          <div key={d.name} className="flex flex-col items-center gap-1 group" title={`${d.name}: ${d.count} active`}>
            <div className="rounded-sm transition-all group-hover:brightness-150" style={{
              width: 18, height: Math.max(4, (d.count / max) * 22),
              background: getAvatarColor(d.name).color + "60",
            }} />
            <span className="text-[10px] text-[#555] font-semibold">{getInitials(d.name)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   FOCUS BANNER — "Needs attention"
   ═══════════════════════════════════════ */
function FocusBanner({ tasks, onFilterOverdue }) {
  const [dismissed, setDismissed] = useState(false);
  const stats = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0);
    let overdue = 0, dueToday = 0, inReview = 0;
    tasks.forEach(t => {
      if (t.status === "Done") return;
      if (t.status === "Review") inReview++;
      if (t.due_date) {
        const due = new Date(t.due_date + "T00:00:00"); due.setHours(0,0,0,0);
        const diff = Math.round((due - now) / 86400000);
        if (diff < 0) overdue++;
        if (diff === 0) dueToday++;
      }
    });
    return { overdue, dueToday, inReview };
  }, [tasks]);

  if (dismissed || (stats.overdue === 0 && stats.dueToday === 0 && stats.inReview === 0)) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-500/10 bg-gradient-to-r from-red-500/[.04] to-amber-500/[.02]">
      <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
        <Flame size={16} className="text-red-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-white">Needs attention</p>
        <p className="text-xs text-[#999]">
          {stats.overdue > 0 && <><strong className="text-red-400">{stats.overdue} overdue</strong> · </>}
          {stats.inReview > 0 && <>{stats.inReview} reviews waiting · </>}
          {stats.dueToday > 0 && <>{stats.dueToday} due today</>}
        </p>
      </div>
      {stats.overdue > 0 && (
        <button onClick={onFilterOverdue} className="px-3 py-1.5 text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors whitespace-nowrap">
          Show overdue
        </button>
      )}
      <button onClick={() => setDismissed(true)} className="text-[#666] hover:text-[var(--t1)] transition-colors p-1"><X size={14} /></button>
    </div>
  );
}

/* ═══════════════════════════════════════
   QUICK ADD ROW
   ═══════════════════════════════════════ */
function QuickAddRow({ onAdd }) {
  const [value, setValue] = useState("");
  const handleSubmit = () => {
    if (!value.trim()) return;
    onAdd(value.trim());
    setValue("");
  };
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg transition-colors focus-within:border-[var(--bdr)]">
      <Plus size={16} className="text-[#333] flex-shrink-0" />
      <input type="text" value={value} onChange={e => setValue(e.target.value)}
        onKeyDown={e => e.key === "Enter" && handleSubmit()}
        placeholder="Quick add a task... (press Enter)"
        className="flex-1 bg-transparent text-sm text-[var(--t1)] placeholder:text-[#333] outline-none" />
      {value && <button onClick={handleSubmit} className="px-3 py-1 text-xs bg-[var(--s2)] border border-[var(--bdrh)] rounded-md text-[#888] hover:text-[var(--t1)] hover:bg-[var(--s2)] transition-colors">Add</button>}
    </div>
  );
}

/* ═══════════════════════════════════════
   ASSIGNEE FILTER (multi-select)
   ═══════════════════════════════════════ */
function AssigneeFilter({ tasks, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const assignees = useMemo(() => {
    const map = {};
    tasks.forEach(t => { if (t.assignee_id && t.assignee_name) map[t.assignee_id] = t.assignee_name; });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tasks]);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors flex items-center gap-1 ${selected.length > 0 ? "bg-blue-500/10 border-blue-500/30 text-blue-400" : "bg-transparent border-[var(--bdr)] text-[#555] hover:text-[#999]"}`}>
        <Users size={12} /> {selected.length > 0 ? `${selected.length} member${selected.length > 1 ? "s" : ""}` : "All Members"}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-xl py-1 min-w-[180px] max-h-[240px] overflow-y-auto">
          {selected.length > 0 && <button onClick={() => onChange([])} className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-[var(--s2)]">Clear all</button>}
          {assignees.map(([id, name]) => (
            <label key={id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--s2)] cursor-pointer">
              <input type="checkbox" checked={selected.includes(id)} onChange={() => toggle(id)} className="rounded border-[var(--bdrh)] bg-transparent" />
              <span className="text-xs text-[var(--t2)]">{name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   TABLE LIST VIEW — with sections, inline edit, hover preview, sticky headers
   ═══════════════════════════════════════ */
function TaskTable({ tasks, navigate, onComplete, onDelete, onUpdate, showAssignee, selectedIds, onToggleSelect, onContextMenu }) {
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [collapsedSections, setCollapsedSections] = useState(new Set(["Done"]));
  const [hoverTask, setHoverTask] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const hoverTimer = useRef(null);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };

  const toggleSection = (status) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  };

  // Group by status
  const grouped = useMemo(() => {
    const order = ["In Progress", "Review", "Open", "Paused", "Done"];
    const groups = {};
    order.forEach(s => groups[s] = []);
    tasks.forEach(t => {
      const s = t.status || "Open";
      if (!groups[s]) groups[s] = [];
      groups[s].push(t);
    });
    // Sort within groups
    if (sortBy) {
      const dir = sortDir === "asc" ? 1 : -1;
      Object.values(groups).forEach(arr => arr.sort((a, b) => {
        let va, vb;
        if (sortBy === "title") { va = a.title || ""; vb = b.title || ""; }
        else if (sortBy === "assignee") { va = a.assignee_name || ""; vb = b.assignee_name || ""; }
        else if (sortBy === "priority") {
          const pOrder = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
          va = pOrder[a.priority] ?? 4; vb = pOrder[b.priority] ?? 4;
          return (va - vb) * dir;
        }
        else if (sortBy === "due") { va = a.due_date || "9999"; vb = b.due_date || "9999"; }
        return va < vb ? -dir : va > vb ? dir : 0;
      }));
    } else {
      // Default: sort by due date, overdue first
      Object.values(groups).forEach(arr => arr.sort((a, b) => {
        const da = a.due_date || "9999"; const db = b.due_date || "9999";
        return da < db ? -1 : da > db ? 1 : 0;
      }));
    }
    return order.filter(s => groups[s].length > 0).map(s => ({ status: s, tasks: groups[s] }));
  }, [tasks, sortBy, sortDir]);

  const handleMouseEnter = (e, task) => {
    hoverTimer.current = setTimeout(() => setHoverTask(task), 700);
    setHoverPos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    setHoverTask(null);
  };

  const SortHeader = ({ field, children, className = "" }) => (
    <th onClick={() => toggleSort(field)}
      className={`px-3 py-2.5 text-xs font-semibold text-[#555] tracking-wider uppercase text-left cursor-pointer select-none hover:text-[#999] transition-colors font-heading sticky top-0 bg-[var(--s1)] z-10 ${className}`}>
      <span className="flex items-center gap-1">{children} <ArrowUpDown size={10} className="opacity-30" /></span>
    </th>
  );

  if (tasks.length === 0) {
    return (
      <div className="text-center py-20 rounded-xl border border-[var(--bdr)] bg-[var(--s1)]">
        <CheckCircle2 size={40} className="mx-auto text-green-500/20 mb-3" />
        <p className="text-[#555] text-sm font-medium">No tasks match your filters</p>
        <p className="text-[#333] text-xs mt-1">Try adjusting your filters or create a new task</p>
      </div>
    );
  }

  return (
    <>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--bdr)]">
            <th className="w-10 px-3 py-2.5 sticky top-0 bg-[var(--s1)] z-10" />
            <SortHeader field="title" className="min-w-[280px]">Task</SortHeader>
            {showAssignee && <SortHeader field="assignee" className="w-[150px]">Assignee</SortHeader>}
            <SortHeader field="priority" className="w-[100px]">Priority</SortHeader>
            <th className="w-[120px] px-3 py-2.5 text-xs font-semibold text-[#555] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading">Status</th>
            <SortHeader field="due" className="w-[150px]">Due Date</SortHeader>
            <th className="w-[70px] sticky top-0 bg-[var(--s1)] z-10" />
          </tr>
        </thead>
        <tbody>
          {grouped.map(group => (
            <SectionGroup key={group.status} group={group} collapsed={collapsedSections.has(group.status)}
              onToggle={() => toggleSection(group.status)} navigate={navigate}
              onComplete={onComplete} onDelete={onDelete} onUpdate={onUpdate}
              showAssignee={showAssignee} selectedIds={selectedIds} onToggleSelect={onToggleSelect}
              onContextMenu={onContextMenu} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
          ))}
        </tbody>
      </table>
      {hoverTask && <HoverPreview task={hoverTask} x={hoverPos.x} y={hoverPos.y} />}
    </>
  );
}

function SectionGroup({ group, collapsed, onToggle, navigate, onComplete, onDelete, onUpdate, showAssignee, selectedIds, onToggleSelect, onContextMenu, onMouseEnter, onMouseLeave }) {
  return (
    <>
      <tr>
        <td colSpan={showAssignee ? 7 : 6} className="p-0">
          <button onClick={onToggle}
            className="w-full flex items-center gap-2 px-4 py-2 bg-[var(--s1)] border-b border-[var(--bdr)] hover:bg-[var(--s1)] transition-colors cursor-pointer select-none">
            <ChevronRight size={12} className={`text-[#666] transition-transform ${collapsed ? "" : "rotate-90"}`} />
            <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOT_COLORS[group.status] }} />
            <span className="text-xs font-semibold text-[#777] tracking-wider font-heading">{group.status.toUpperCase()}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--s2)] text-[#555]">{group.tasks.length}</span>
          </button>
        </td>
      </tr>
      {!collapsed && group.tasks.map(t => (
        <TaskTableRow key={t.task_id} t={t} navigate={navigate}
          onComplete={onComplete} onDelete={onDelete} onUpdate={onUpdate}
          showAssignee={showAssignee} selected={selectedIds.has(t.task_id)}
          onToggleSelect={() => onToggleSelect(t.task_id)}
          onContextMenu={onContextMenu}
          onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
      ))}
    </>
  );
}

function TaskTableRow({ t, navigate, onComplete, onDelete, onUpdate, showAssignee, selected, onToggleSelect, onContextMenu, onMouseEnter, onMouseLeave }) {
  const due = getDueInfo(t.due_date);
  const subtaskCount = (t.subtasks || []).length;
  const subtaskDone = (t.subtasks || []).filter(s => s.done).length;
  const avatarColor = getAvatarColor(t.assignee_name);
  const isReview = t.status === "Review";
  const isDone = t.status === "Done";

  return (
    <tr className={`border-b border-[var(--bdr)] cursor-pointer transition-colors group
      ${due?.overdue && !isDone ? "bg-red-500/[.015] hover:bg-red-500/[.03]" : due?.today && !isDone ? "bg-amber-500/[.01] hover:bg-amber-500/[.02]" : "hover:bg-white/[.02]"}
      ${selected ? "bg-blue-500/[.05]" : ""}`}
      onClick={() => navigate(`/tasks/${t.task_id}`)}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu(e, t); }}
      onMouseEnter={(e) => onMouseEnter(e, t)}
      onMouseLeave={onMouseLeave}>
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        {!isDone ? (
          <button onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
            className={`w-[18px] h-[18px] rounded border transition-colors flex items-center justify-center flex-shrink-0
              ${selected ? "bg-blue-500/20 border-blue-500/50" : "border-[var(--bdrh)] hover:border-green-400 hover:bg-green-400/10"}`}>
            {selected && <span className="text-blue-400 text-xs">✓</span>}
          </button>
        ) : (
          <div className="w-[18px] h-[18px] rounded bg-green-500/15 border border-green-500/30 flex items-center justify-center">
            <span className="text-green-400 text-xs">✓</span>
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        <p className={`text-sm font-medium leading-tight ${isDone ? "text-[#666] line-through" : "text-white/90"}`}>{t.title}</p>
        <div className="flex items-center gap-2 mt-1 text-xs flex-wrap">
          {t.brief_title && <span className="text-cyan-500/70">{t.brief_title}</span>}
          {!t.brief_title && t.lead_name && <span className="text-purple-400/70">{t.lead_name}</span>}
          {!t.brief_title && !t.lead_name && t.project_name && <span className="text-blue-400/70">{t.project_name}</span>}
          {subtaskCount > 0 && (
            <span className="flex items-center gap-1.5 text-[#555]">
              <span className="inline-block w-8 h-[3px] bg-[var(--s2)] rounded-full overflow-hidden">
                <span className="block h-full bg-green-500 rounded-full" style={{ width: `${(subtaskDone / subtaskCount) * 100}%` }} />
              </span>
              {subtaskDone}/{subtaskCount}
            </span>
          )}
        </div>
      </td>
      {showAssignee && (
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
              style={{ background: avatarColor.bg, color: avatarColor.color }}>
              {getInitials(t.assignee_name)}
            </div>
            <span className="text-sm text-[var(--t1)]/70 truncate">{t.assignee_name || "Unassigned"}</span>
          </div>
        </td>
      )}
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <PriorityDropdown taskId={t.task_id} currentPriority={t.priority || "Medium"} onUpdate={onUpdate} />
      </td>
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <StatusDropdown taskId={t.task_id} currentStatus={t.status} onUpdate={onUpdate} />
      </td>
      <td className="px-3 py-2.5">
        {due ? (
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-[#888]">{due.formatted}</span>
            {due.text && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${due.bg} ${due.color}`}>{due.text}</span>}
          </div>
        ) : <span className="text-sm text-[#555]">No date</span>}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isReview && (
            <>
              <button onClick={(e) => { e.stopPropagation(); api.put(`/tasks/${t.task_id}`, { status: "Done" }).then(onUpdate); toast.success("Approved!"); }}
                className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors">✓</button>
              <button onClick={(e) => { e.stopPropagation(); api.put(`/tasks/${t.task_id}`, { status: "In Progress" }).then(onUpdate); toast.success("Returned"); }}
                className="text-xs px-2 py-1 rounded bg-[var(--s2)] text-[#666] hover:bg-[var(--s3)] transition-colors">↩</button>
            </>
          )}
          {!isReview && !isDone && (
            <button onClick={(e) => { e.stopPropagation(); onComplete(t.task_id); }}
              className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors">✓ Done</button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onContextMenu(e, t); }}
            className="text-xs px-1.5 py-1 rounded bg-[var(--s2)] text-[#555] hover:bg-[var(--s3)] transition-colors">
            <MoreHorizontal size={12} />
          </button>
        </div>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════
   BOARD VIEW (Kanban)
   ═══════════════════════════════════════ */
function BoardView({ tasks, navigate, onUpdate, onComplete }) {
  const columns = useMemo(() => {
    const cols = { "Open": [], "In Progress": [], "Review": [], "Done": [] };
    tasks.forEach(t => {
      const s = t.status || "Open";
      if (!cols[s]) cols[s] = [];
      cols[s].push(t);
    });
    // Sort each column by due date
    Object.values(cols).forEach(arr => arr.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999")));
    return Object.entries(cols);
  }, [tasks]);

  const handleDrop = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      onUpdate();
      toast.success(`Moved to ${newStatus}`);
    } catch { toast.error("Failed to move task"); }
  };

  if (tasks.length === 0) {
    return (
      <div className="text-center py-20 rounded-xl border border-[var(--bdr)] bg-[var(--s1)]">
        <LayoutGrid size={40} className="mx-auto text-[#222] mb-3" />
        <p className="text-[#555] text-sm font-medium">No tasks to display</p>
        <p className="text-[#333] text-xs mt-1">Create a task to see it on the board</p>
      </div>
    );
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
      {columns.map(([status, columnTasks]) => (
        <div key={status} className="flex-shrink-0 w-full sm:w-[290px] bg-[var(--s1)] border border-[var(--bdr)] rounded-xl flex flex-col max-h-[70vh]"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("taskId"); if (id) handleDrop(id, status); }}>
          <div className="flex items-center justify-between px-3 py-3 border-b border-[var(--bdr)] flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOT_COLORS[status] }} />
              <span className="text-xs font-semibold text-[#777] tracking-wider font-heading">{status.toUpperCase()}</span>
            </div>
            <span className="text-xs text-[#666] bg-[var(--s2)] px-2 py-0.5 rounded-full">{columnTasks.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
            {columnTasks.map(t => (
              <BoardCard key={t.task_id} t={t} navigate={navigate} onUpdate={onUpdate} onComplete={onComplete} />
            ))}
            {columnTasks.length === 0 && (
              <div className="text-center py-8 text-xs text-[#555] border-2 border-dashed border-[var(--bdr)] rounded-lg">
                {status === "Done" ? "Completed tasks appear here" : "Drop tasks here"}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function BoardCard({ t, navigate, onUpdate, onComplete }) {
  const due = getDueInfo(t.due_date);
  const avatarColor = getAvatarColor(t.assignee_name);
  const subtaskCount = (t.subtasks || []).length;
  const subtaskDone = (t.subtasks || []).filter(s => s.done).length;
  const isDone = t.status === "Done";

  return (
    <div className={`bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-3.5 cursor-pointer transition-all hover:border-[var(--bdrh)] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20 relative group ${isDone ? "opacity-50" : ""}`}
      draggable onDragStart={(e) => e.dataTransfer.setData("taskId", t.task_id)}
      onClick={() => navigate(`/tasks/${t.task_id}`)}>
      {/* Priority bar */}
      <div className="absolute top-0 right-3 w-[3px] h-5 rounded-b" style={{ background: PRIORITY_COLORS[t.priority] || "#666" }} />
      <p className={`text-sm font-medium leading-tight mb-2 ${isDone ? "line-through text-[#666]" : "text-white/90"}`}>{t.title}</p>
      <div className="flex gap-1.5 flex-wrap mb-2">
        {t.brief_title && <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400/80">{t.brief_title}</span>}
        {t.lead_name && <span className="text-xs px-2 py-0.5 rounded bg-purple-400/10 text-purple-400/80">{t.lead_name}</span>}
        {subtaskCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-400/80">{subtaskDone}/{subtaskCount} ✓</span>}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-[var(--bdr)]">
        <div className="flex items-center gap-1.5 text-xs">
          <Calendar size={11} className="text-[#666]" />
          {due ? (
            <span className={due.overdue ? "text-red-400" : due.today ? "text-amber-400" : "text-[#555]"}>
              {due.text || due.formatted}
            </span>
          ) : <span className="text-[#555]">No date</span>}
        </div>
        {t.assignee_name && (
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold"
            style={{ background: avatarColor.bg, color: avatarColor.color }} title={t.assignee_name}>
            {getInitials(t.assignee_name)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   GANTT / TIMELINE VIEW
   ═══════════════════════════════════════ */
function GanttChart({ tasks, navigate }) {
  const scrollRef = useRef(null);
  const [zoom, setZoom] = useState("2week"); // "week", "2week", "month"
  const [hoveredTask, setHoveredTask] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  const zoomDayWidth = zoom === "week" ? 64 : zoom === "month" ? 28 : 44;

  const { startDate, endDate, totalDays, dayWidth } = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0);
    let minDate = new Date(now); let maxDate = new Date(now);
    const daysAhead = zoom === "week" ? 14 : zoom === "month" ? 45 : 30;
    maxDate.setDate(maxDate.getDate() + daysAhead);
    tasks.forEach(t => {
      if (t.start_date) { const sd = new Date(t.start_date + "T00:00:00"); if (sd < minDate) minDate = new Date(sd); }
      if (t.due_date) { const dd = new Date(t.due_date + "T00:00:00"); if (dd > maxDate) maxDate = new Date(dd); }
      if (t.created_at) { const cd = new Date(t.created_at); cd.setHours(0,0,0,0); if (cd < minDate) minDate = new Date(cd); }
    });
    minDate.setDate(minDate.getDate() - 2);
    maxDate.setDate(maxDate.getDate() + 5);
    const total = Math.max(Math.round((maxDate - minDate) / 86400000), 14);
    return { startDate: minDate, endDate: maxDate, totalDays: total, dayWidth: zoomDayWidth };
  }, [tasks, zoom, zoomDayWidth]);

  const grouped = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const key = t.assignee_id || "unassigned";
      const name = t.assignee_name || "Unassigned";
      if (!map[key]) map[key] = { name, tasks: [], overdue: 0 };
      map[key].tasks.push(t);
      if (t.due_date && t.status !== "Done") {
        const now = new Date(); now.setHours(0,0,0,0);
        const due = new Date(t.due_date + "T00:00:00"); due.setHours(0,0,0,0);
        if (due < now) map[key].overdue++;
      }
    });
    return Object.entries(map).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [tasks]);

  const dateHeaders = useMemo(() => {
    const headers = []; const d = new Date(startDate);
    for (let i = 0; i < totalDays; i++) { headers.push(new Date(d)); d.setDate(d.getDate() + 1); }
    return headers;
  }, [startDate, totalDays]);

  const getBarPosition = (task) => {
    const taskStart = task.start_date ? new Date(task.start_date + "T00:00:00") : (task.created_at ? new Date(task.created_at) : new Date());
    taskStart.setHours(0,0,0,0);
    const taskEnd = task.due_date ? new Date(task.due_date + "T00:00:00") : new Date(taskStart);
    taskEnd.setHours(0,0,0,0);
    if (taskEnd <= taskStart) taskEnd.setDate(taskStart.getDate() + 1);
    return {
      left: Math.max(0, (taskStart - startDate) / 86400000) * dayWidth,
      width: Math.max(1, (taskEnd - taskStart) / 86400000) * dayWidth,
      color: GANTT_STATUS_COLORS[task.status] || "#64748B",
      isOverdue: task.due_date && task.status !== "Done" && new Date(task.due_date + "T00:00:00") < new Date(new Date().setHours(0,0,0,0)),
    };
  };

  const todayOffset = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0);
    return ((now - startDate) / 86400000) * dayWidth;
  }, [startDate, dayWidth]);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-20 rounded-xl border border-[var(--bdr)] bg-[var(--s1)]">
        <BarChart3 size={40} className="mx-auto text-[#222] mb-3" />
        <p className="text-[#555] text-sm font-medium">No tasks to show on timeline</p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl overflow-hidden">
      {/* Toolbar with zoom */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--bdr)]">
        <span className="text-xs text-[#888] font-semibold tracking-wider font-heading">TEAM TIMELINE</span>
        <div className="flex gap-1">
          {[{ key: "week", label: "Week" }, { key: "2week", label: "2 Weeks" }, { key: "month", label: "Month" }].map(z => (
            <button key={z.key} onClick={() => setZoom(z.key)}
              className={`px-3 py-1 rounded-md text-xs border transition-colors ${zoom === z.key ? "bg-[var(--s2)] text-white border-[var(--bdrh)]" : "border-[var(--bdr)] text-[#888] hover:text-[#999]"}`}>
              {z.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex">
        {/* Left panel */}
        <div className="flex-shrink-0 w-[220px] border-r border-[var(--bdr)]">
          <div className="h-[50px] flex items-center px-3 border-b border-[var(--bdr)]">
            <span className="text-xs text-[#888] font-semibold tracking-wider font-heading">TEAM / TASK</span>
          </div>
          {grouped.map(([key, group]) => (
            <div key={key}>
              <div className="h-[32px] flex items-center gap-2 px-3 bg-[var(--s1)] border-b border-[var(--bdr)]">
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold"
                  style={{ ...getAvatarColor(group.name) && { background: getAvatarColor(group.name).bg, color: getAvatarColor(group.name).color } }}>
                  {getInitials(group.name)}
                </div>
                <span className="text-xs font-semibold text-[var(--t2)] truncate">{group.name}</span>
                {group.overdue > 0 && <span className="ml-auto text-xs text-red-400 font-semibold">{group.overdue} overdue</span>}
              </div>
              {group.tasks.map(t => (
                <div key={t.task_id} className="h-[40px] flex items-center px-3 pl-8 border-b border-[var(--bdr)] cursor-pointer hover:bg-white/[.02]"
                  onClick={() => navigate(`/tasks/${t.task_id}`)}>
                  <span className="text-xs text-[var(--t2)] truncate">{t.title}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        {/* Timeline */}
        <div className="flex-1 overflow-x-auto" ref={scrollRef}>
          <div style={{ width: totalDays * dayWidth, minWidth: "100%" }}>
            <div className="flex h-[50px] border-b border-[var(--bdr)]">
              {dateHeaders.map((d, i) => {
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isToday = d.toDateString() === new Date().toDateString();
                return (
                  <div key={i} style={{ width: dayWidth }}
                    className={`flex flex-col items-center justify-center border-r border-[var(--bdr)] ${isToday ? "bg-[var(--r)]/5" : isWeekend ? "bg-white/[.005]" : ""}`}>
                    <span className={`text-[10px] ${isToday ? "text-[#E31937]" : isWeekend ? "text-white/15" : "text-white/25"}`}>
                      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()]}
                    </span>
                    <span className={`text-xs font-medium ${isToday ? "text-[#E31937] font-bold" : isWeekend ? "text-white/15" : "text-[var(--t3)]"}`}>
                      {isToday ? "Today" : d.getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
            {grouped.map(([key, group]) => (
              <div key={key}>
                <div className="h-[32px] border-b border-[var(--bdr)] bg-[var(--s1)] relative">
                  <div className="absolute top-0 bottom-0 w-px bg-[var(--r)]/25" style={{ left: todayOffset }} />
                </div>
                {group.tasks.map(t => {
                  const bar = getBarPosition(t);
                  const dueInfo = getDueInfo(t.due_date);
                  return (
                    <div key={t.task_id} className="h-[44px] border-b border-[var(--bdr)] relative">
                      <div className="absolute top-0 bottom-0 w-[2px] bg-[var(--r)]/20" style={{ left: todayOffset }} />
                      <div className="absolute top-[6px] rounded-md cursor-pointer transition-all flex items-center px-2.5 gap-1.5 hover:shadow-lg hover:shadow-black/30 hover:z-10 hover:scale-y-110"
                        style={{
                          left: bar.left, width: Math.max(bar.width, 32), height: 32,
                          backgroundColor: bar.isOverdue ? "rgba(239,68,68,.15)" : bar.color + "20",
                          borderLeft: `3px solid ${bar.isOverdue ? "#EF4444" : bar.color}`,
                        }}
                        onClick={() => navigate(`/tasks/${t.task_id}`)}
                        onMouseEnter={(e) => { setHoveredTask(t); setHoverPos({ x: e.clientX, y: e.clientY }); }}
                        onMouseLeave={() => setHoveredTask(null)}>
                        <span className="text-xs text-white/80 truncate whitespace-nowrap font-medium">{t.title}</span>
                        {bar.isOverdue && <span className="text-[11px] text-red-400 ml-auto flex-shrink-0 font-semibold">overdue</span>}
                        {!bar.isOverdue && dueInfo?.text && bar.width > 150 && <span className="text-[11px] text-[var(--t2)] ml-auto flex-shrink-0">{dueInfo.text}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Hover tooltip */}
      {hoveredTask && (
        <div className="fixed z-[80] bg-[var(--s1)] border border-[var(--bdrh)] rounded-xl shadow-2xl p-3 w-[260px] pointer-events-none"
          style={{ left: Math.min(hoverPos.x + 16, window.innerWidth - 280), top: Math.min(hoverPos.y - 10, window.innerHeight - 150) }}>
          <p className="text-sm font-medium text-white mb-1">{hoveredTask.title}</p>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span style={{ color: STATUS_DOT_COLORS[hoveredTask.status] }}>{hoveredTask.status}</span>
            {hoveredTask.assignee_name && <span className="text-[#888]">{hoveredTask.assignee_name}</span>}
            {hoveredTask.priority && <span style={{ color: PRIORITY_COLORS[hoveredTask.priority] }}>{hoveredTask.priority}</span>}
            {hoveredTask.due_date && <span className="text-[#888]">{hoveredTask.due_date}</span>}
          </div>
          {hoveredTask.brief_title && <p className="text-xs text-cyan-400/60 mt-1">{hoveredTask.brief_title}</p>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   MY TASKS DASHBOARD (Focus view)
   ═══════════════════════════════════════ */
function MyTasksDashboard({ tasks, navigate, onComplete, onDelete, onUpdate, mode = "todo" }) {
  const sections = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0);
    const overdue = [], today = [], thisWeek = [], pending = [], rest = [];
    tasks.forEach(t => {
      if (t.status === "Done") return;
      if (t.due_date) {
        const due = new Date(t.due_date + "T00:00:00"); due.setHours(0,0,0,0);
        const diff = Math.round((due - now) / 86400000);
        if (diff < 0) { overdue.push(t); return; }
        if (diff === 0) { today.push(t); return; }
        if (diff <= 7) { thisWeek.push(t); return; }
      }
      // For review mode, group non-date tasks as "pending review"
      if (mode === "review") { pending.push(t); return; }
      rest.push(t);
    });
    const sortByDue = (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999");
    overdue.sort(sortByDue); today.sort(sortByDue); thisWeek.sort(sortByDue); pending.sort(sortByDue); rest.sort(sortByDue);
    return { overdue, today, thisWeek, pending, rest };
  }, [tasks, mode]);

  const done = tasks.filter(t => t.status === "Done");

  const DashboardSection = ({ title, icon: Icon, iconColor, tasks: sectionTasks, emptyMsg }) => {
    if (sectionTasks.length === 0 && emptyMsg) {
      return (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2 dm-section-header">
            <Icon size={14} className={iconColor} />
            <span className="text-sm font-semibold text-[#888] font-heading">{title}</span>
          </div>
          <div className="text-center py-6 rounded-lg border border-dashed border-[var(--bdr)] text-sm text-[#555]">{emptyMsg}</div>
        </div>
      );
    }
    if (sectionTasks.length === 0) return null;
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 dm-section-header">
          <Icon size={14} className={iconColor} />
          <span className="text-sm font-semibold text-[#888] font-heading">{title}</span>
          <span className="text-xs text-[#666] bg-[var(--s2)] px-2 py-0.5 rounded-full">{sectionTasks.length}</span>
        </div>
        <div className="space-y-2">
          {sectionTasks.map(t => (
            <MyTaskRow key={t.task_id} t={t} navigate={navigate} onComplete={onComplete} onDelete={onDelete} onUpdate={onUpdate} showAssignee={mode === "delegated"} showReviewActions={mode === "review"} />
          ))}
        </div>
      </div>
    );
  };

  const emptyMessages = {
    todo: "No tasks assigned to you — you're all clear!",
    review: "No tasks waiting for your review",
    delegated: "No tasks delegated to others",
  };

  return (
    <div>
      <DashboardSection title="OVERDUE" icon={AlertTriangle} iconColor="text-red-400" tasks={sections.overdue}
        emptyMsg={mode === "todo" ? "No overdue tasks — you're on track!" : null} />
      <DashboardSection title={mode === "review" ? "REVIEW TODAY" : "TODAY'S FOCUS"} icon={Flame} iconColor="text-amber-400" tasks={sections.today}
        emptyMsg={mode === "todo" ? "Nothing due today" : null} />
      <DashboardSection title="THIS WEEK" icon={Calendar} iconColor="text-blue-400" tasks={sections.thisWeek} />
      {mode === "review" && <DashboardSection title="PENDING REVIEW" icon={Clock} iconColor="text-purple-400" tasks={sections.pending} />}
      {mode !== "review" && <DashboardSection title="UPCOMING" icon={ChevronRight} iconColor="text-[#888]" tasks={sections.rest} />}
      {sections.overdue.length === 0 && sections.today.length === 0 && sections.thisWeek.length === 0 && sections.pending.length === 0 && sections.rest.length === 0 && done.length === 0 && (
        <div className="text-center py-12 rounded-xl border border-dashed border-[var(--bdr)] text-sm text-[#555]">{emptyMessages[mode]}</div>
      )}
      {done.length > 0 && (
        <details className="mb-6">
          <summary className="flex items-center gap-2 mb-2 cursor-pointer text-sm font-semibold text-[#888] font-heading">
            <CheckCircle2 size={14} className="text-green-400/50" /> DONE <span className="text-xs text-[#555] bg-[var(--s2)] px-2 py-0.5 rounded-full ml-1">{done.length}</span>
          </summary>
          <div className="space-y-2">
            {done.map(t => <MyTaskRow key={t.task_id} t={t} navigate={navigate} onComplete={onComplete} onDelete={onDelete} onUpdate={onUpdate} showAssignee={mode === "delegated"} />)}
          </div>
        </details>
      )}
    </div>
  );
}

function MyTaskRow({ t, navigate, onComplete, onDelete, onUpdate, showAssignee = false, showReviewActions = false }) {
  const due = getDueInfo(t.due_date);
  const isDone = t.status === "Done";
  const subtaskCount = (t.subtasks || []).length;
  const subtaskDone = (t.subtasks || []).filter(s => s.done).length;
  const avatarColor = getAvatarColor(t.assignee_name);
  const swipeRef = useRef({ startX: 0, swiping: false });

  const onTouchStart = (e) => { if (window.innerWidth > 768) return; swipeRef.current.startX = e.touches[0].clientX; swipeRef.current.swiping = true; };
  const onTouchMove = (e) => { if (!swipeRef.current.swiping) return; const diff = e.touches[0].clientX - swipeRef.current.startX; const el = e.currentTarget; const clamped = Math.max(-60, Math.min(100, diff)); el.style.transform = `translateX(${clamped}px)`; el.style.transition = 'none'; if (clamped > 50) el.style.borderColor = 'rgba(34,197,94,.3)'; else el.style.borderColor = ''; };
  const onTouchEnd = (e) => { if (!swipeRef.current.swiping) return; swipeRef.current.swiping = false; const el = e.currentTarget; const diff = parseFloat(el.style.transform?.replace(/[^0-9.-]/g, '') || 0); el.style.transition = 'transform .2s, border-color .2s'; el.style.transform = 'translateX(0)'; el.style.borderColor = ''; if (diff > 70 && !isDone) { el.style.transform = 'translateX(100%)'; el.style.opacity = '0.3'; setTimeout(() => { el.style.transform = 'translateX(0)'; el.style.opacity = '1'; onComplete(t.task_id); }, 200); } };

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors cursor-pointer group dm-task-row
      ${due?.overdue && !isDone ? "border-red-500/10 bg-red-500/[.02] hover:bg-red-500/[.04]" : "border-[var(--bdr)] bg-[var(--s1)] hover:bg-[var(--s1)]"}`}
      onClick={() => navigate(`/tasks/${t.task_id}`)}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {!isDone ? (
        <button onClick={(e) => { e.stopPropagation(); onComplete(t.task_id); }}
          className="w-5 h-5 rounded border border-[var(--bdrh)] hover:border-green-400 hover:bg-green-400/10 transition-colors flex-shrink-0 dm-task-checkbox" />
      ) : (
        <div className="w-5 h-5 rounded bg-green-500/15 border border-green-500/30 flex items-center justify-center text-green-400 text-xs flex-shrink-0">✓</div>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium dm-task-title ${isDone ? "text-[#666] line-through" : "text-white/90"}`}>{t.title}</p>
        <div className="flex items-center gap-2 mt-0.5 text-xs flex-wrap dm-task-meta">
          <span style={{ color: PRIORITY_COLORS[t.priority] || "#666" }}>{t.priority}</span>
          {t.brief_title && <span className="text-cyan-500/60">{t.brief_title}</span>}
          {showAssignee && t.assignee_name && <span className="text-blue-400/60">→ {t.assignee_name}</span>}
          {subtaskCount > 0 && <span className="text-[#555]">{subtaskDone}/{subtaskCount} subtasks</span>}
        </div>
      </div>
      {due && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs text-[#888]">{due.formatted}</span>
          {due.text && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${due.bg} ${due.color}`}>{due.text}</span>}
        </div>
      )}
      {showReviewActions && !isDone && (
        <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          <button onClick={(e) => { e.stopPropagation(); api.put(`/tasks/${t.task_id}`, { status: "Done" }).then(onUpdate); toast.success("Approved!"); }}
            className="text-xs px-3 py-1.5 rounded-md bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors font-medium">✓ Approve</button>
          <button onClick={(e) => { e.stopPropagation(); api.put(`/tasks/${t.task_id}`, { status: "In Progress" }).then(onUpdate); toast.success("Returned for revision"); }}
            className="text-xs px-3 py-1.5 rounded-md bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors font-medium">↩ Revise</button>
        </div>
      )}
      {!showReviewActions && !isDone && (
        <div onClick={(e) => e.stopPropagation()}>
          <StatusDropdown taskId={t.task_id} currentStatus={t.status} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   KEYBOARD SHORTCUTS BAR
   ═══════════════════════════════════════ */
function ShortcutBar({ visible }) {
  if (!visible) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex gap-4 px-5 py-2 bg-[var(--s1)]/95 border border-[var(--bdr)] rounded-xl backdrop-blur-sm"
      style={{ animation: "fadeIn .3s ease-out" }}>
      {[
        { key: "/", label: "Search" }, { key: "N", label: "New task" },
        { key: "1", label: "List" }, { key: "2", label: "Board" }, { key: "3", label: "Timeline" },
      ].map(s => (
        <div key={s.key} className="flex items-center gap-1.5 text-xs text-[#777]">
          <kbd className="px-1.5 py-0.5 bg-[var(--s2)] border border-[var(--bdrh)] rounded text-xs text-[#aaa] font-heading">{s.key}</kbd>
          {s.label}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */
export default function TasksListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState(() => localStorage.getItem("tasks_tab") || "my");
  const [myWork, setMyWork] = useState({ assigned: [], to_review: [], delegated: [] });
  const [mySubTab, setMySubTab] = useState("todo"); // todo, review, delegated
  const [teamTasks, setTeamTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters — URL-backed so reload survives and links are shareable
  const [search, setSearch] = useUrlState("q", "");
  const [statusFilter, setStatusFilter] = useUrlState("status", "all");
  const [priorityFilter, setPriorityFilter] = useUrlState("priority", "all");
  const [dateFilter, setDateFilter] = useUrlState("date", "all");
  // Assignee filter is an array — serialize to CSV in URL
  const [assigneeFilterRaw, setAssigneeFilterRaw] = useUrlState("assignees", "");
  const assigneeFilter = useMemo(
    () => (assigneeFilterRaw ? assigneeFilterRaw.split(",").filter(Boolean) : []),
    [assigneeFilterRaw]
  );
  const setAssigneeFilter = useCallback(
    (arr) => setAssigneeFilterRaw(Array.isArray(arr) ? arr.filter(Boolean).join(",") : ""),
    [setAssigneeFilterRaw]
  );
  const teamView = "list";

  // Selection & context menu
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const searchRef = useRef(null);

  // Load tasks — load both on mount so counts show immediately
  useEffect(() => { loadMyTasks(); loadTeamTasks(); }, []);

  const loadMyTasks = async () => {
    try {
      setLoading(true);
      const res = await api.get("/tasks/my-work");
      setMyWork(res.data || { assigned: [], to_review: [], delegated: [] });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadTeamTasks = async (status) => {
    try {
      setLoading(true);
      const url = status && status !== "all" ? `/tasks/team?status=${status}` : "/tasks/team";
      setTeamTasks((await api.get(url)).data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const completeTask = async (taskId) => {
    await api.post(`/tasks/${taskId}/complete`);
    toast.success("Task completed!");
    reloadCurrent();
  };

  // Delete with optimistic remove + 5s undo window. No confirm modal.
  const deleteTask = async (taskId) => {
    // Snapshot for undo
    const snapshotMy = { ...myWork };
    const snapshotTeam = [...teamTasks];

    // Find the task for toast label
    const findIn = (arr) => (arr || []).find(t => t.task_id === taskId);
    const task =
      findIn(myWork.assigned) ||
      findIn(myWork.to_review) ||
      findIn(myWork.delegated) ||
      findIn(teamTasks);

    // Optimistic remove
    setMyWork(prev => ({
      assigned:  (prev.assigned  || []).filter(t => t.task_id !== taskId),
      to_review: (prev.to_review || []).filter(t => t.task_id !== taskId),
      delegated: (prev.delegated || []).filter(t => t.task_id !== taskId),
    }));
    setTeamTasks(prev => prev.filter(t => t.task_id !== taskId));
    setSelectedIds(prev => { const n = new Set(prev); n.delete(taskId); return n; });

    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(10);

    let undone = false;
    toast(`Deleted: ${(task?.title || "task").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          undone = true;
          setMyWork(snapshotMy);
          setTeamTasks(snapshotTeam);
          if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
        },
      },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try {
            await api.delete(`/tasks/${taskId}`);
          } catch (err) {
            console.error("Delete failed:", err);
            setMyWork(snapshotMy);
            setTeamTasks(snapshotTeam);
            toast.error("Delete failed — restored");
          }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try {
            await api.delete(`/tasks/${taskId}`);
          } catch (err) {
            console.error("Delete failed:", err);
            setMyWork(snapshotMy);
            setTeamTasks(snapshotTeam);
            toast.error("Delete failed — restored");
          }
        }, 50);
      },
    });
  };

  const reloadCurrent = () => { loadMyTasks(); if (tab === "team") loadTeamTasks(statusFilter); };

  // Re-fetch team tasks when status filter changes (backend filters Done/Completed)
  useEffect(() => {
    if (tab === "team") loadTeamTasks(statusFilter);
  }, [statusFilter]);
  usePullToRefresh(reloadCurrent);

  const quickAddTask = async (title) => {
    try {
      await api.post("/tasks", { title, status: "Open", priority: "Medium" });
      toast.success("Task created!");
      reloadCurrent();
    } catch { toast.error("Failed to create task"); }
  };

  // Toggle selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      if (action === "done") {
        await Promise.all(ids.map(id => api.post(`/tasks/${id}/complete`)));
        toast.success(`${ids.length} tasks completed`);
      } else if (action === "delete") {
        if (!window.confirm(`Delete ${ids.length} tasks?`)) return;
        await Promise.all(ids.map(id => api.delete(`/tasks/${id}`)));
        toast.success(`${ids.length} tasks deleted`);
      } else if (action === "status") {
        const newStatus = prompt("Enter new status (Open, In Progress, Review, Paused, Done):");
        if (!newStatus || !STATUS_OPTIONS.includes(newStatus)) return;
        await Promise.all(ids.map(id => api.put(`/tasks/${id}`, { status: newStatus })));
        toast.success(`${ids.length} tasks → ${newStatus}`);
      } else if (action === "priority") {
        const newPriority = prompt("Enter priority (Urgent, High, Medium, Low):");
        if (!newPriority) return;
        await Promise.all(ids.map(id => api.put(`/tasks/${id}`, { priority: newPriority })));
        toast.success(`${ids.length} tasks → ${newPriority}`);
      }
      setSelectedIds(new Set());
      reloadCurrent();
    } catch { toast.error("Bulk action failed"); }
  };

  // Context menu actions
  const handleContextAction = async (action, task) => {
    if (action === "open") navigate(`/tasks/${task.task_id}`);
    else if (action === "copy") { navigator.clipboard.writeText(`${window.location.origin}/tasks/${task.task_id}`); toast.success("Link copied"); }
    else if (action === "start") { await api.put(`/tasks/${task.task_id}`, { status: "In Progress" }); toast.success("Started"); reloadCurrent(); }
    else if (action === "pause") { await api.put(`/tasks/${task.task_id}`, { status: "Paused" }); toast.success("Paused"); reloadCurrent(); }
    else if (action === "review") { await api.put(`/tasks/${task.task_id}`, { status: "Review" }); toast.success("Sent to review"); reloadCurrent(); }
    else if (action === "done") { await api.post(`/tasks/${task.task_id}/complete`); toast.success("Completed!"); reloadCurrent(); }
    else if (action === "delete") deleteTask(task.task_id);
  };

  // Keyboard cursor for the displayed task list (desktop only)
  const [cursorIdx, setCursorIdx] = useState(-1);

  // Show shortcuts briefly
  useEffect(() => {
    const timer = setTimeout(() => setShowShortcuts(true), 1000);
    const hide = setTimeout(() => setShowShortcuts(false), 5000);
    return () => { clearTimeout(timer); clearTimeout(hide); };
  }, []);

  // Filtered team tasks
  const displayedTeamTasks = useMemo(() => {
    let result = [...teamTasks];
    if (statusFilter !== "all") result = result.filter(t => t.status === statusFilter);
    if (priorityFilter !== "all") result = result.filter(t => (t.priority || "").toLowerCase() === priorityFilter);
    if (assigneeFilter.length > 0) result = result.filter(t => assigneeFilter.includes(t.assignee_id));
    if (dateFilter !== "all") {
      result = result.filter(t => {
        if (!t.due_date) return false;
        const now = new Date(); now.setHours(0,0,0,0);
        const due = new Date(t.due_date + "T00:00:00"); due.setHours(0,0,0,0);
        const diff = Math.round((due - now) / 86400000);
        if (dateFilter === "overdue") return diff < 0;
        if (dateFilter === "today") return diff === 0;
        if (dateFilter === "3days") return diff >= 0 && diff <= 3;
        if (dateFilter === "7days") return diff >= 0 && diff <= 7;
        return true;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t => t.title?.toLowerCase().includes(q) || t.assignee_name?.toLowerCase().includes(q) || t.brief_title?.toLowerCase().includes(q) || t.lead_name?.toLowerCase().includes(q));
    }
    return result;
  }, [teamTasks, statusFilter, priorityFilter, assigneeFilter, dateFilter, search]);

  // Current my tasks list based on sub-tab
  const currentMyTasks = useMemo(() => {
    if (mySubTab === "review") return myWork.to_review;
    if (mySubTab === "delegated") return myWork.delegated;
    return myWork.assigned;
  }, [myWork, mySubTab]);

  // Filtered my tasks (for dashboard, just search)
  const displayedMyTasks = useMemo(() => {
    let result = [...currentMyTasks];
    if (search) result = result.filter(t => t.title?.toLowerCase().includes(search.toLowerCase()));
    return result;
  }, [currentMyTasks, search]);

  const myTotalCount = myWork.assigned.length + myWork.to_review.length + myWork.delegated.length;

  // Stats for team
  const teamStats = useMemo(() => {
    const s = { total: teamTasks.length, inProgress: 0, review: 0, open: 0, overdue: 0, done: 0 };
    const now = new Date(); now.setHours(0,0,0,0);
    teamTasks.forEach(t => {
      if (t.status === "In Progress") s.inProgress++;
      if (t.status === "Review") s.review++;
      if (t.status === "Open") s.open++;
      if (t.status === "Done") s.done++;
      if (t.due_date && t.status !== "Done") {
        const due = new Date(t.due_date + "T00:00:00"); due.setHours(0,0,0,0);
        if (due < now) s.overdue++;
      }
    });
    return s;
  }, [teamTasks]);

  // ═══════ Keyboard shortcuts — single source of truth ═══════
  // The list the cursor navigates depends on the current tab.
  const cursorList = useMemo(
    () => (tab === "team" ? displayedTeamTasks : displayedMyTasks),
    [tab, displayedTeamTasks, displayedMyTasks]
  );

  const clampCursor = useCallback((i) => {
    if (!cursorList.length) return -1;
    return ((i % cursorList.length) + cursorList.length) % cursorList.length;
  }, [cursorList.length]);

  // One-key status / priority change on the cursor row
  const quickUpdate = useCallback(async (patch) => {
    const t = cursorList[cursorIdx];
    if (!t) return;
    // Optimistic: update in local list immediately
    const apply = (list) => list.map(x => x.task_id === t.task_id ? { ...x, ...patch } : x);
    setMyWork(prev => ({
      assigned: apply(prev.assigned || []),
      to_review: apply(prev.to_review || []),
      delegated: apply(prev.delegated || []),
    }));
    setTeamTasks(prev => apply(prev));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    try {
      await api.put(`/tasks/${t.task_id}`, patch);
      const label = patch.status ? `Status → ${patch.status}` : patch.priority ? `Priority → ${patch.priority}` : "Updated";
      toast.success(label);
    } catch {
      toast.error("Update failed — refreshing");
      reloadCurrent();
    }
  }, [cursorList, cursorIdx]);  // eslint-disable-line react-hooks/exhaustive-deps

  useShortcuts(
    [
      // Search / new — replace the old hand-rolled /,n handler
      { keys: "/", description: "Focus search", group: "Tasks",
        handler: () => searchRef.current?.focus() },
      { keys: "n", description: "New task", group: "Tasks",
        handler: () => navigate("/tasks/new") },

      // Cursor nav
      { keys: "j", description: "Next task", group: "Tasks",
        handler: () => setCursorIdx(c => clampCursor(c + 1)) },
      { keys: "k", description: "Previous task", group: "Tasks",
        handler: () => setCursorIdx(c => clampCursor(c - 1)) },
      { keys: "enter", description: "Open task", group: "Tasks",
        handler: () => {
          const t = cursorList[cursorIdx];
          if (t) navigate(`/tasks/${t.task_id}`);
        } },

      // Complete / delete via the existing handlers
      { keys: "c", description: "Complete task", group: "Tasks",
        handler: () => {
          const t = cursorList[cursorIdx];
          if (t) { completeTask(t.task_id); }
        } },
      { keys: "x", description: "Toggle select (bulk)", group: "Tasks",
        handler: () => {
          const t = cursorList[cursorIdx];
          if (t) toggleSelect(t.task_id);
        } },

      // One-key status: o/i/r/p/d
      ...Object.entries(STATUS_KEY_MAP).map(([key, status]) => ({
        keys: key,
        description: `Status → ${status}`,
        group: "Status",
        handler: () => quickUpdate({ status }),
      })),

      // One-key priority: u/h/m/l
      ...Object.entries(PRIORITY_KEY_MAP).map(([key, priority]) => ({
        keys: key,
        description: `Priority → ${priority}`,
        group: "Priority",
        handler: () => quickUpdate({ priority }),
      })),

      // Bulk / clear
      { keys: "escape", description: "Clear selection",
        handler: () => { setSelectedIds(new Set()); setContextMenu(null); setCursorIdx(-1); } },
    ],
    { enabled: !isMobile && !loading }
  );

  // Clamp cursor when list shrinks (after delete/complete)
  useEffect(() => {
    if (cursorIdx >= cursorList.length) setCursorIdx(cursorList.length - 1);
  }, [cursorList.length, cursorIdx]);

  if (loading && myTotalCount === 0 && teamTasks.length === 0) {
    if (isMobile) return <MobileSkeleton type="list" />;
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] text-xl font-heading">LOADING...</div></div>;
  }

  // ═══════ MOBILE VIEW — IG STYLE TASKS ═══════
  if (isMobile) {
    const mobileChips = [
      { key: "todo", label: "To Do", count: myWork.assigned.length },
      { key: "review", label: "Reviews", count: myWork.to_review.length },
      { key: "delegated", label: "Delegated", count: myWork.delegated.length },
    ];
    const activeTasks = currentMyTasks.filter(t => t.status !== "Done");
    const doneTasks = currentMyTasks.filter(t => t.status === "Done");
    const overdue = [], today = [], thisWeek = [], later = [], noDue = [];
    activeTasks.forEach(t => {
      const due = getDueInfo(t.due_date);
      if (!due) { noDue.push(t); return; }
      if (due.overdue) overdue.push(t);
      else if (due.today) today.push(t);
      else if (due.diff !== undefined && due.diff <= 7) thisWeek.push(t);
      else later.push(t);
    });

    const TaskCard = ({ t }) => {
      const due = getDueInfo(t.due_date);
      const [checked, setChecked] = useState(false);
      const leadName = t.lead_name || t.brief_title || '';
      const isProject = t.project_id && !t.lead_id;
      const domain = t.lead_website ? t.lead_website.replace(/https?:\/\//, '').replace(/\/.*/, '') : (leadName ? leadName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '') + '.com' : '');
      const fallbackInitial = (leadName || t.title || '')[0]?.toUpperCase() || '·';
      return (
        <div style={{ background: '#FFF', borderRadius: 12, margin: '0 16px 8px', boxShadow: '0 1px 3px rgba(0,0,0,.08)', overflow: 'hidden', cursor: 'pointer' }}
          onClick={() => navigate(`/tasks/${t.task_id}`)}>
          <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            {/* Circular checkbox */}
            <div onClick={(e) => { e.stopPropagation(); setChecked(true); setTimeout(() => completeTask(t.task_id), 300); }}
              style={{ width: 22, height: 22, borderRadius: '50%', border: checked ? 'none' : '2px solid #C7C7C7', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1, background: checked ? '#262626' : 'transparent', color: '#FFF', fontSize: 11, transition: 'all .2s' }}>
              {checked && '✓'}
            </div>
            {/* Lead favicon or project "P" or initial */}
            {isProject ? (
              <div style={{ width: 24, height: 24, borderRadius: 6, background: '#EFEFEF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#262626', flexShrink: 0, marginTop: 1 }}>P</div>
            ) : domain ? (
              <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'contain', background: '#EFEFEF', flexShrink: 0, marginTop: 1 }} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div style={{ width: 24, height: 24, borderRadius: 6, background: '#EFEFEF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#8E8E8E', flexShrink: 0, marginTop: 1 }}>{fallbackInitial}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#262626', lineHeight: 1.3, fontFamily: 'var(--font-sans)' }}>{t.title}</div>
              <div style={{ fontSize: 11, color: '#8E8E8E', marginTop: 1, fontFamily: 'var(--font-sans)' }}>
                {leadName}{t.priority ? ` · ${t.priority}` : ''}
              </div>
            </div>
            {due && (due.text || due.formatted) && (
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, flexShrink: 0, fontWeight: 600, fontFamily: 'var(--font-mono)',
                background: due.overdue ? '#FEE2E2' : due.today ? '#FEF3C7' : '#ECFDF5',
                color: due.overdue ? '#DC2626' : due.today ? '#D97706' : '#059669' }}>
                {due.text || due.formatted}
              </span>
            )}
          </div>
        </div>
      );
    };

    const Section = ({ label, tasks, labelColor }) => {
      if (tasks.length === 0) return null;
      return <>
        <div style={{ fontSize: 14, fontWeight: 600, color: labelColor || '#262626', padding: '12px 16px 6px' }}>{label}</div>
        {tasks.map(t => <TaskCard key={t.task_id} t={t} />)}
      </>;
    };

    return (
      <div style={{ paddingBottom: 16 }}>
        {/* IG Filter Chips */}
        <div className="ig-scroll" style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, padding: '10px 16px', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {mobileChips.map(c => {
            const isActive = mySubTab === c.key;
            return (
              <button key={c.key} onClick={() => setMySubTab(c.key)}
                style={{ padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: isActive ? '#262626' : '#EFEFEF', color: isActive ? '#FFF' : '#262626', border: 'none', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {c.label} {c.count > 0 && <b style={{ marginLeft: 3 }}>{c.count}</b>}
              </button>
            );
          })}
        </div>

        {/* Inline create */}
        <div onClick={() => navigate('/tasks/new')}
          style={{ margin: '8px 16px 12px', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: '1.5px dashed #DBDBDB', borderRadius: 12, cursor: 'pointer', color: '#8E8E8E', fontSize: 13 }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#0095F6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700 }}>+</div>
          New task...
        </div>

        {/* Grouped sections */}
        <div style={{ padding: '0' }}>
          {activeTasks.length === 0 && doneTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, margin: '0 16px', background: '#FFF', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
              <p style={{ color: '#8E8E8E', fontSize: 13 }}>No tasks yet</p>
            </div>
          ) : <>
            <Section label="Overdue" tasks={overdue} labelColor="#DC2626" />
            <Section label="Today" tasks={today} labelColor="#D97706" />
            <Section label="This Week" tasks={thisWeek} labelColor="#059669" />
            <Section label="Later" tasks={later} />
            {noDue.length > 0 && <Section label="No Due Date" tasks={noDue} />}
            {doneTasks.length > 0 && <Section label="Done" tasks={doneTasks} />}
          </>}
        </div>
      </div>
    );
  }

  // ═══════ DESKTOP VIEW ═══════
  return (
    <div className="dm-page-container max-w-[1200px] mx-auto space-y-4" data-testid="tasks-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl text-[var(--t1)] tracking-wide">TASKS</h1>
        <button onClick={() => navigate("/tasks/new")}
          className="dm-desktop-only flex items-center gap-2 px-5 py-2.5 bg-[var(--r)] text-white text-[13px] font-bold rounded-full hover:bg-[var(--r)] transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-500/20 font-heading">
          <Plus size={16} /> New Task
          <kbd className="ml-1 px-1.5 py-0.5 bg-[var(--s3)] rounded text-[10px]">N</kbd>
        </button>
      </div>

      {/* Mobile inline create */}
      <div className="dm-mobile-only" style={{ display: 'none', margin: '0 16px 12px', alignItems: 'center', gap: 8, padding: '10px 14px', border: '1.5px dashed var(--bdr)', borderRadius: 12, cursor: 'pointer' }} onClick={() => navigate('/tasks/new')}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--rd)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--r)', fontSize: 16, fontWeight: 700 }}>+</div>
        <span style={{ fontSize: 13, color: 'var(--t3)' }}>New task...</span>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 p-1 bg-[var(--s1)] rounded-lg border border-[var(--bdr)] w-fit">
        <button onClick={() => { setTab("my"); localStorage.setItem("tasks_tab", "my"); setSelectedIds(new Set()); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium transition-colors ${tab === "my" ? "bg-[var(--s2)] text-white" : "text-[#666] hover:text-[var(--t1)]"}`}>
          <User size={14} /> My Tasks <span className="text-xs px-2 py-0.5 rounded-full bg-white/[.06] text-[#888]">{myTotalCount}</span>
        </button>
        <button onClick={() => { setTab("team"); localStorage.setItem("tasks_tab", "team"); setSelectedIds(new Set()); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium transition-colors ${tab === "team" ? "bg-[var(--s2)] text-white" : "text-[#666] hover:text-[var(--t1)]"}`}>
          <Users size={14} /> Team Tasks <span className="text-xs px-2 py-0.5 rounded-full bg-white/[.06] text-[#888]">{teamTasks.length}</span>
        </button>
      </div>

      {/* ═══════ MY TASKS TAB ═══════ */}
      {tab === "my" && (
        <>
          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
            <input ref={searchRef} type="text" placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-sm placeholder:text-[var(--t3)] focus:outline-none focus:border-[#E31937] focus:shadow-[0_0_0_3px_rgba(227,25,55,.08)] transition-all" />
          </div>
          {/* Sub-tabs: To Do / To Review / Delegated */}
          <div className="flex gap-1 p-1 bg-[var(--s1)] rounded-lg border border-[var(--bdr)] w-fit dm-task-tabs">
            {[
              { key: "todo", label: "To Do", count: myWork.assigned.filter(t => t.status !== "Done").length, icon: "📋" },
              { key: "review", label: "To Review", count: myWork.to_review.filter(t => t.status !== "Done").length, icon: "👁" },
              { key: "delegated", label: "Delegated", count: myWork.delegated.filter(t => t.status !== "Done").length, icon: "📤" },
            ].map(s => (
              <button key={s.key} onClick={() => setMySubTab(s.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mySubTab === s.key ? "bg-[var(--s2)] text-white" : "text-[#666] hover:text-[var(--t1)]"}`}>
                <span>{s.icon}</span> {s.label}
                {s.count > 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${mySubTab === s.key ? "bg-[var(--s3)] text-white" : "bg-[var(--s2)] text-[#888]"}`}>{s.count}</span>}
              </button>
            ))}
          </div>
          {/* Dashboard view */}
          <MyTasksDashboard tasks={displayedMyTasks} navigate={navigate} onComplete={completeTask} onDelete={deleteTask} onUpdate={reloadCurrent} mode={mySubTab} />
        </>
      )}

      {/* ═══════ TEAM TASKS TAB ═══════ */}
      {tab === "team" && (
        <>
          {/* Focus Banner */}
          <FocusBanner tasks={teamTasks} onFilterOverdue={() => setDateFilter("overdue")} />

          {/* Search + View toggle */}
          <div className="flex items-center gap-3 dm-team-filter-bar">
            <div className="relative flex-1 max-w-[360px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
              <input ref={tab === "team" ? searchRef : undefined} type="text" placeholder="Search tasks, people, briefs..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-[13px] placeholder:text-[var(--t3)] focus:outline-none focus:border-[#E31937] focus:shadow-[0_0_0_3px_rgba(227,25,55,.08)] transition-all" />
            </div>
            {/* Filters */}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[12px] text-[#999] outline-none cursor-pointer">
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[12px] text-[#999] outline-none cursor-pointer">
              <option value="all">Any Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <AssigneeFilter tasks={teamTasks} selected={assigneeFilter} onChange={setAssigneeFilter} />
            <select value={dateFilter} onChange={e => setDateFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-[12px] text-[#999] outline-none cursor-pointer">
              <option value="all">All Dates</option>
              <option value="overdue">Overdue</option>
              <option value="today">Due Today</option>
              <option value="3days">Next 3 Days</option>
              <option value="7days">Next 7 Days</option>
            </select>
          </div>

          {/* Stats bar + workload */}
          <div className="flex items-center gap-4 px-4 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl dm-workload-heatmap">
            {[
              { label: "In Progress", count: teamStats.inProgress, color: "#3B82F6" },
              { label: "Review", count: teamStats.review, color: "#F59E0B" },
              { label: "Open", count: teamStats.open, color: "#64748B" },
              { label: "Overdue", count: teamStats.overdue, color: "#EF4444" },
              { label: "Done", count: teamStats.done, color: "#22C55E" },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1.5 text-sm text-[#777]">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                {s.label} <span className="font-semibold text-[var(--t3)]">{s.count}</span>
              </div>
            ))}
            <WorkloadHeatmap tasks={teamTasks} />
          </div>

          {/* New Task button (full form with reviewer) */}
          {teamView === "list" && (
            <button onClick={() => navigate("/tasks/new")}
              className="flex items-center gap-3 px-4 py-2.5 bg-[var(--s1)] border border-dashed border-[var(--bdr)] rounded-lg text-sm text-[var(--t3)] hover:border-[var(--bdrh)] hover:text-[var(--t1)] transition-colors w-full">
              <Plus size={16} className="flex-shrink-0" />
              <span>New task...</span>
            </button>
          )}

          {/* Task count */}
          <div className="text-xs text-[var(--t2)]">
            {displayedTeamTasks.length} task{displayedTeamTasks.length !== 1 ? "s" : ""}
            {assigneeFilter.length > 0 && ` · filtered by ${assigneeFilter.length} member${assigneeFilter.length > 1 ? "s" : ""}`}
          </div>

          {/* Views */}
          {teamView === "list" && (
            <div className="rounded-xl border border-[var(--bdr)] overflow-hidden">
              <TaskTable tasks={displayedTeamTasks} navigate={navigate} onComplete={completeTask} onDelete={deleteTask}
                onUpdate={reloadCurrent} showAssignee={true} selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onContextMenu={(e, t) => setContextMenu({ x: e.clientX, y: e.clientY, task: t })} />
            </div>
          )}
        </>
      )}

      {/* Bulk actions bar */}
      <BulkActionsBar count={selectedIds.size} onAction={handleBulkAction} onClear={() => setSelectedIds(new Set())} />

      {/* Context menu */}
      {contextMenu && <ContextMenu x={contextMenu.x} y={contextMenu.y} task={contextMenu.task}
        onClose={() => setContextMenu(null)} onAction={handleContextAction} />}

      {/* Keyboard shortcuts */}
      <ShortcutBar visible={showShortcuts} />

      <style>{`
        @keyframes slideUp { from { transform: translate(-50%, 20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #222; border-radius: 2px; }
      `}</style>
    </div>
  );
}
